import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { useLocalSearchParams } from 'expo-router';
import React, { useMemo, useState } from 'react';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { PlayerAvatar, ratingTone } from '@/components/match/PlayerRatings';
import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { CardBoundary } from '@/components/ui/CardBoundary';
import { Segmented } from '@/components/ui/Segmented';
import { VerticalPitchMarkings } from '@/components/ui/VerticalPitchMarkings';
import { palette, useThemePalette } from '@/config/theme';
import { useMatchXg } from '@/features/matches/hooks';
import { bzzoiroCutout } from '@/lib/data/provider';
import type { LineupPayload } from '@/lib/data/schemas';
import {
  PITCH_VERTICAL_PADDING,
  PLAYER_TOKEN_WIDTH,
  ROW_GAP,
  ROW_MIN_HEIGHT,
  TOKEN_AVATAR_SIZE,
} from '@/lib/pitch/formation';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeTeamName } from '@/lib/i18n/footballNames';

/** Per-player match contribution (goals/assists/cards) + minutes, keyed by AF id. */
export interface PlayerMark {
  goals: number;
  assists: number;
  yellow: number;
  red: number;
  minutes: number | null;
  captain: boolean;
  /** Started on the bench (came on, or unused sub). */
  substitute: boolean;
}

/** Overlay maps keyed by API-Football id (lineup player.id). */
export interface LineupOverlay {
  photoByAf: Map<number, string>;
  ratingByAf: Map<number, number>;
  /** Live/finished per-player events, so the pitch can badge goals and cards. */
  markByAf?: Map<number, PlayerMark>;
  /** Each player's CLUB crest (365-style badge on the avatar). */
  clubLogoByAf?: Map<number, string>;
}

/** A small club crest badge, sat in an avatar corner (national-team matches). */
function ClubCrest({ uri, size = 16 }: { uri: string; size?: number }) {
  return (
    <View
      className="items-center justify-center overflow-hidden rounded-full bg-deep/90"
      style={{ width: size, height: size }}
    >
      <Image
        source={{ uri }}
        style={{ width: size - 3, height: size - 3 }}
        contentFit="contain"
        cachePolicy="memory-disk"
        transition={120}
      />
    </View>
  );
}

/** One unavailable player (injured / suspended), per team side. */
export interface InjuryItem {
  name: string;
  photo: string | null;
  reason: string; // "Injury" | "Suspended" | "Missing Fixture" | …
}
export interface InjuriesBySide {
  home: InjuryItem[];
  away: InjuryItem[];
}

interface GridPlayer {
  name: string;
  number: number | null;
  afId: number | null;
  row: number;
  col: number;
}

function lastName(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts[parts.length - 1] || name;
}

/**
 * Fold a player name for cross-provider matching: lowercase, strip accents,
 * drop every non-alphanumeric. Mirrors how the rated (Bzzoiro) names and the
 * API-Football lineup names are compared so e.g. "N'Golo Kanté" === "ngolo kante".
 */
function normName(raw: string): string {
  return raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '');
}

/** Two-letter initials from a player's name, for the no-photo fallback. */
function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();
}

/**
 * A pitch avatar that tries an ORDERED list of sources (Bzzoiro cut-out → the
 * existing API-Football photo) and advances to the next on a load error, finally
 * landing on the player's initials. Mirrors XgPlayerRatings' PlayerPhoto chain so
 * a rated player gets the same transparent cut-out the ratings card shows, while
 * an unmatched player keeps their flat photo (passed as the sole source).
 */
function ChainAvatar({
  sources,
  name,
  size,
}: {
  sources: (string | null | undefined)[];
  name: string;
  size: number;
}) {
  const { candidates, key } = React.useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const src of sources) {
      if (src && !seen.has(src)) {
        seen.add(src);
        out.push(src);
      }
    }
    return { candidates: out, key: out.join('|') };
  }, [sources]);

  const [index, setIndex] = React.useState(0);
  const [seenKey, setSeenKey] = React.useState(key);
  if (seenKey !== key) {
    setSeenKey(key);
    setIndex(0);
  }

  const uri = index < candidates.length ? candidates[index] : null;
  if (!uri) {
    return (
      <View
        className="items-center justify-center rounded-full bg-surface-2"
        style={{ width: size, height: size }}
      >
        <AppText variant="micro" weight="bold" className="text-muted">
          {initialsOf(name)}
        </AppText>
      </View>
    );
  }
  return (
    <Image
      key={uri}
      source={{ uri }}
      style={{ width: size, height: size, borderRadius: size / 2 }}
      contentFit="cover"
      contentPosition="top"
      transition={150}
      cachePolicy="memory-disk"
      onError={() => setIndex((current) => current + 1)}
      accessibilityLabel={name}
    />
  );
}

function parseGrid(lineup: LineupPayload): GridPlayer[][] {
  const players: GridPlayer[] = [];
  for (const entry of lineup.startXI ?? []) {
    const grid = entry.player.grid;
    if (!grid) continue;
    const [rowText, colText] = grid.split(':');
    const row = Number.parseInt(rowText ?? '', 10);
    const col = Number.parseInt(colText ?? '', 10);
    if (!Number.isFinite(row) || !Number.isFinite(col)) continue;
    players.push({
      name: entry.player.name ?? '-',
      number: entry.player.number ?? null,
      afId: entry.player.id ?? null,
      row,
      col,
    });
  }
  const maxRow = players.reduce((max, player) => Math.max(max, player.row), 0);
  const rows: GridPlayer[][] = [];
  for (let row = 1; row <= maxRow; row += 1) {
    rows.push(players.filter((player) => player.row === row).sort((a, b) => a.col - b.col));
  }
  return rows.filter((row) => row.length > 0);
}

/** Small rating badge pinned to a pitch avatar's corner. */
function RatingBadge({ rating }: { rating: number }) {
  const { arabicNumerals } = useLocale();
  const tone = ratingTone(rating);
  return (
    <View
      className="absolute -bottom-1 -end-1 items-center justify-center rounded"
      style={{ backgroundColor: tone.bg, minWidth: 20, height: 15, paddingHorizontal: 2 }}
    >
      <AppText weight="bold" tabular style={{ color: tone.fg, fontSize: 9 }}>
        {localizeDigits(rating.toFixed(1), arabicNumerals)}
      </AppText>
    </View>
  );
}

/** Goal pip (ball + count) pinned to a pitch avatar's corner. */
function GoalPip({ count }: { count: number }) {
  const palette = useThemePalette();
  const { arabicNumerals } = useLocale();
  return (
    <View className="flex-row items-center gap-0.5 rounded-full bg-deep/90 px-1 py-0.5">
      <Ionicons name="football" size={10} color={palette.voltBright} />
      {count > 1 ? (
        <AppText weight="bold" tabular style={{ color: palette.voltBright, fontSize: 8 }}>
          {localizeDigits(String(count), arabicNumerals)}
        </AppText>
      ) : null}
    </View>
  );
}

/** Tiny "A" assist marker. */
function AssistPip() {
  return (
    <View className="items-center justify-center rounded-full bg-deep/90" style={{ width: 14, height: 14 }}>
      <AppText weight="bold" style={{ color: palette.soft, fontSize: 8 }}>
        A
      </AppText>
    </View>
  );
}

/** Yellow / red booking square. */
function CardPip({ red }: { red: boolean }) {
  const palette = useThemePalette();
  return (
    <View style={{ width: 9, height: 12, borderRadius: 2, backgroundColor: red ? palette.loss : '#F4D03F' }} />
  );
}

/**
 * A single player token on the turf: photo (or numbered ring) with a centred
 * rating pill underneath (365-style) and corner badges - number, goals/assist,
 * booking, and a sub-off arrow.
 */
function PlayerDot({
  player,
  overlay,
  cutoutByName,
  subbedOff,
  onPress,
}: {
  player: GridPlayer;
  overlay?: LineupOverlay;
  /** normalized player name → Bzzoiro cut-out portrait URL (rated players). */
  cutoutByName?: Map<string, string>;
  subbedOff: boolean;
  onPress?: (afId: number) => void;
}) {
  const palette = useThemePalette();
  const { arabicNumerals } = useLocale();
  const photo = player.afId != null ? overlay?.photoByAf.get(player.afId) : undefined;
  // A transparent cut-out for this player, matched by normalized name against the
  // match's RATED (Bzzoiro) players. When present it leads the avatar source chain
  // (cut-out → existing photo → initials); otherwise the flat photo path is kept.
  const cutout = cutoutByName?.get(normName(player.name));
  const rating = player.afId != null ? overlay?.ratingByAf.get(player.afId) : undefined;
  const mark = player.afId != null ? overlay?.markByAf?.get(player.afId) : undefined;
  const clubLogo = player.afId != null ? overlay?.clubLogoByAf?.get(player.afId) : undefined;
  const goals = mark?.goals ?? 0;
  const assists = mark?.assists ?? 0;
  const red = (mark?.red ?? 0) > 0;
  const yellow = !red && (mark?.yellow ?? 0) > 0;
  const captain = mark?.captain ?? false;
  const ringColor = captain ? palette.captain : palette.volt;
  const tone = rating != null ? ratingTone(rating) : null;
  const tappable = onPress != null && player.afId != null;
  // A real portrait (cut-out or flat photo) is shown — otherwise the numbered
  // ring is the avatar and the small jersey-number corner badge is redundant.
  const hasAvatar = Boolean(cutout || photo);

  return (
    <Pressable
      disabled={!tappable}
      accessibilityRole={tappable ? 'button' : undefined}
      onPress={() => player.afId != null && onPress?.(player.afId)}
      className="items-center gap-1 active:opacity-70"
      style={{ width: PLAYER_TOKEN_WIDTH }}
    >
      <View
        style={{ width: TOKEN_AVATAR_SIZE, height: TOKEN_AVATAR_SIZE }}
        className="items-center justify-center"
      >
        {cutout ? (
          <View
            style={{ borderRadius: TOKEN_AVATAR_SIZE / 2, borderWidth: 1.5, borderColor: ringColor }}
            className="overflow-hidden"
          >
            <ChainAvatar
              sources={[cutout, photo]}
              name={player.name}
              size={TOKEN_AVATAR_SIZE - 4}
            />
          </View>
        ) : photo ? (
          <View
            style={{ borderRadius: TOKEN_AVATAR_SIZE / 2, borderWidth: 1.5, borderColor: ringColor }}
            className="overflow-hidden"
          >
            <PlayerAvatar uri={photo} name={player.name} size={TOKEN_AVATAR_SIZE - 4} />
          </View>
        ) : (
          <View
            className="h-10 w-10 items-center justify-center rounded-full border"
            style={{ borderColor: ringColor, backgroundColor: 'rgba(34,211,255,0.16)' }}
          >
            <AppText variant="micro" weight="bold" tabular className="text-volt-bright">
              {player.number !== null ? localizeDigits(String(player.number), arabicNumerals) : '-'}
            </AppText>
          </View>
        )}

        {/* Top-start: jersey number (the no-photo fallback shows it large instead). */}
        {hasAvatar && player.number !== null ? (
          <View
            className="absolute -start-1 -top-1 items-center justify-center rounded-full bg-deep/90"
            style={{ minWidth: 15, height: 15, paddingHorizontal: 2 }}
          >
            <AppText weight="bold" tabular style={{ color: palette.soft, fontSize: 8 }}>
              {localizeDigits(String(player.number), arabicNumerals)}
            </AppText>
          </View>
        ) : null}

        {/* Top-end: captain badge, then live goal / assist pips (stacked). */}
        {captain || goals > 0 || assists > 0 ? (
          <View className="absolute -end-1.5 -top-1.5 items-center gap-0.5">
            {captain ? (
              <View
                className="items-center justify-center rounded-full"
                style={{ width: 15, height: 15, backgroundColor: palette.captain }}
              >
                <AppText weight="bold" style={{ color: '#1A1408', fontSize: 9 }}>
                  C
                </AppText>
              </View>
            ) : null}
            {goals > 0 ? <GoalPip count={goals} /> : null}
            {assists > 0 ? <AssistPip /> : null}
          </View>
        ) : null}

        {/* Bottom-end: the club crest in a FIXED spot - bookings never cover it. */}
        {clubLogo ? (
          <View className="absolute -bottom-1 -end-1"><ClubCrest uri={clubLogo} size={16} /></View>
        ) : null}

        {/* Bottom-start: bookings + sub-off arrow, kept clear of the club crest. */}
        {red || yellow || subbedOff ? (
          <View className="absolute -bottom-1 -start-1 items-center gap-0.5">
            {red || yellow ? <CardPip red={red} /> : null}
            {subbedOff ? (
              <View
                className="items-center justify-center rounded-full"
                style={{ width: 15, height: 15, backgroundColor: palette.loss }}
              >
                <Ionicons name="caret-down" size={9} color="#FFFFFF" />
              </View>
            ) : null}
          </View>
        ) : null}
      </View>

      {tone ? (
        <View className="rounded px-1" style={{ backgroundColor: tone.bg, minWidth: 24 }}>
          <AppText weight="bold" tabular style={{ color: tone.fg, fontSize: 9, textAlign: 'center' }}>
            {localizeDigits((rating as number).toFixed(1), arabicNumerals)}
          </AppText>
        </View>
      ) : null}

      <View className="rounded bg-deep/85 px-1">
        <AppText variant="micro" className="text-center text-ink" numberOfLines={1}>
          {lastName(player.name)}
        </AppText>
      </View>
    </Pressable>
  );
}

/**
 * Horizontal mowing bands over the grass - alternating a touch lighter / darker
 * green, the classic top-down broadcast stripe pattern. Kept low-opacity so it
 * reads as mowing, never garish.
 */
function TurfStripes() {
  return (
    <View className="absolute inset-0" style={{ pointerEvents: 'none' }}>
      {Array.from({ length: 7 }).map((_, index) => (
        <View
          key={index}
          className="flex-1"
          style={{ backgroundColor: index % 2 === 0 ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}
        />
      ))}
    </View>
  );
}

/**
 * Floodlight depth: the turf is a hair lighter at the top (under the lights),
 * sinking to a deeper green at the bottom. Sits above the stripes, below the
 * markings + players.
 */
function PitchDepth() {
  return (
    <LinearGradient
      colors={['rgba(255,255,255,0.07)', 'rgba(255,255,255,0)', 'rgba(0,0,0,0.12)']}
      locations={[0, 0.5, 1]}
      style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
      pointerEvents="none"
    />
  );
}

/** ONE team on its own full pitch - keeper at the bottom, attack at the top. */
function SingleTeamPitch({
  lineup,
  overlay,
  cutoutByName,
  onPlayerPress,
}: {
  lineup: LineupPayload;
  overlay?: LineupOverlay;
  cutoutByName?: Map<string, string>;
  onPlayerPress?: (afId: number) => void;
}) {
  const { arabicNumerals, locale } = useLocale();
  const rows = useMemo(() => parseGrid(lineup), [lineup]);

  // Sub-off heuristic: a starter whose minutes trail the team's max (someone who
  // played to the whistle) was taken off. Red-carded players show a card instead,
  // so we exclude them. Works for live (minutes = current elapsed) and finished.
  const subOff = useMemo(() => {
    const marks = overlay?.markByAf;
    const off = new Set<number>();
    if (!marks) return off;
    const starterAfs = rows
      .flat()
      .map((player) => player.afId)
      .filter((id): id is number => id != null);
    const minutesList = starterAfs
      .map((af) => marks.get(af)?.minutes)
      .filter((value): value is number => typeof value === 'number');
    if (minutesList.length === 0) return off;
    const maxMinutes = Math.max(...minutesList);
    for (const af of starterAfs) {
      const mark = marks.get(af);
      if (mark?.minutes != null && mark.minutes < maxMinutes - 3 && mark.red === 0) off.add(af);
    }
    return off;
  }, [overlay?.markByAf, rows]);

  return (
    <Card className="overflow-hidden p-0">
      <View className="flex-row items-center justify-between bg-surface-2/50 px-4 py-2.5">
        <AppText variant="caption" weight="bold" className="shrink text-ink" numberOfLines={1}>
          {localizeTeamName(lineup.team, locale)}
        </AppText>
        {lineup.formation ? (
          <AppText variant="micro" weight="bold" tabular className="text-volt">
            {localizeDigits(lineup.formation, arabicNumerals)}
          </AppText>
        ) : null}
      </View>
      <View className="overflow-hidden bg-pitch">
        <TurfStripes />
        <PitchDepth />
        <VerticalPitchMarkings />
        {/* Rows are evenly distributed top→bottom (space-between) so the pitch
            always fills its card — never cramped on phones nor stretched on
            tablets — while each row reserves ROW_MIN_HEIGHT so the rating chip
            under a player can never overlap the row below. */}
        <View
          className="justify-between"
          style={{
            rowGap: ROW_GAP,
            paddingVertical: PITCH_VERTICAL_PADDING,
            minHeight: rows.length * ROW_MIN_HEIGHT + PITCH_VERTICAL_PADDING * 2,
          }}
        >
          {[...rows].reverse().map((row, index) => (
            <View
              key={index}
              className="flex-row items-center justify-evenly"
              style={{ minHeight: ROW_MIN_HEIGHT }}
            >
              {row.map((player, col) => (
                <PlayerDot
                  key={`${player.number ?? 'n'}-${col}`}
                  player={player}
                  overlay={overlay}
                  cutoutByName={cutoutByName}
                  subbedOff={player.afId != null && subOff.has(player.afId)}
                  onPress={onPlayerPress}
                />
              ))}
            </View>
          ))}
        </View>
      </View>
    </Card>
  );
}

const BENCH_ORDER = ['GK', 'DEF', 'MID', 'FWD'] as const;
type BenchGroup = (typeof BENCH_ORDER)[number];

/** Map an API-Football position letter (G/D/M/F) to our bench group. */
function posGroup(pos: string | null | undefined): BenchGroup {
  const p = (pos ?? '').toUpperCase();
  return p === 'G' ? 'GK' : p === 'D' ? 'DEF' : p === 'M' ? 'MID' : 'FWD';
}

/** Bench, grouped by position (keepers → defence → midfield → attack). */
function BenchCard({
  lineup,
  overlay,
  onPlayerPress,
}: {
  lineup: LineupPayload;
  overlay?: LineupOverlay;
  onPlayerPress?: (afId: number) => void;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { arabicNumerals } = useLocale();
  const bench = lineup.substitutes ?? [];
  if (bench.length === 0) return null;

  const groups: Record<BenchGroup, typeof bench> = { GK: [], DEF: [], MID: [], FWD: [] };
  for (const entry of bench) groups[posGroup(entry.player.pos)].push(entry);

  return (
    <Card className="overflow-hidden">
      <View className="flex-row items-center gap-2 border-b border-line bg-surface-2/50 px-4 py-2.5">
        <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-soft">
          {t('match.bench')}
        </AppText>
      </View>
      <View className="px-4 py-2">
        {BENCH_ORDER.filter((group) => groups[group].length > 0).map((group) => (
          <View key={group} className="gap-2 py-1.5">
            <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-muted">
              {t(`player.positions.${group}`)}
            </AppText>
            <View className="flex-row flex-wrap gap-x-4 gap-y-3">
              {groups[group].map((entry, index) => {
                const af = entry.player.id ?? null;
                const photo = af != null ? overlay?.photoByAf.get(af) : undefined;
                const rating = af != null ? overlay?.ratingByAf.get(af) : undefined;
                const mark = af != null ? overlay?.markByAf?.get(af) : undefined;
                const clubLogo = af != null ? overlay?.clubLogoByAf?.get(af) : undefined;
                const cameOn = (mark?.minutes ?? 0) > 0;
                const tappable = onPlayerPress != null && af != null;
                return (
                  <Pressable
                    key={index}
                    disabled={!tappable}
                    accessibilityRole={tappable ? 'button' : undefined}
                    onPress={() => af != null && onPlayerPress?.(af)}
                    className="flex-row items-center gap-2 active:opacity-70"
                    style={{ width: '46%' }}
                  >
                    <View>
                      <PlayerAvatar uri={photo} name={entry.player.name ?? '-'} size={32} />
                      {rating != null ? (
                        <RatingBadge rating={rating} />
                      ) : clubLogo ? (
                        <View className="absolute -bottom-1 -end-1">
                          <ClubCrest uri={clubLogo} size={14} />
                        </View>
                      ) : null}
                      {mark?.captain ? (
                        <View
                          className="absolute -start-1 -top-1 items-center justify-center rounded-full"
                          style={{ width: 13, height: 13, backgroundColor: palette.captain }}
                        >
                          <AppText weight="bold" style={{ color: '#1A1408', fontSize: 7 }}>
                            C
                          </AppText>
                        </View>
                      ) : null}
                    </View>
                    <View className="flex-1">
                      <View className="flex-row items-center gap-1">
                        {cameOn ? <Ionicons name="caret-up" size={11} color={palette.win} /> : null}
                        <AppText
                          variant="micro"
                          weight="semibold"
                          className="flex-1 text-ink"
                          numberOfLines={1}
                        >
                          {entry.player.name ?? '-'}
                        </AppText>
                      </View>
                      {entry.player.number != null ? (
                        <AppText variant="micro" className="text-muted" tabular>
                          #{localizeDigits(String(entry.player.number), arabicNumerals)}
                        </AppText>
                      ) : null}
                    </View>
                  </Pressable>
                );
              })}
            </View>
          </View>
        ))}
      </View>
    </Card>
  );
}

/** The team's coach: photo (or initials) + name, as its own card. */
function CoachCard({ lineup }: { lineup: LineupPayload }) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const coach = lineup.coach;
  if (!coach?.name) return null;
  return (
    <Card className="overflow-hidden">
      <View className="flex-row items-center gap-3 px-4 py-3">
        <View style={{ borderRadius: 22, borderWidth: 1.5, borderColor: palette.voltLine }} className="overflow-hidden">
          <PlayerAvatar uri={coach.photo ?? null} name={coach.name} size={40} />
        </View>
        <View className="flex-1">
          <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-muted">
            {t('match.coach')}
          </AppText>
          <AppText variant="caption" weight="bold" className="text-ink" numberOfLines={1}>
            {coach.name}
          </AppText>
        </View>
      </View>
    </Card>
  );
}

/** Reason → translated label + tone (red = injury, amber = suspension). */
function reasonStyle(reason: string, t: (k: string, o?: Record<string, unknown>) => string) {
  const lower = reason.toLowerCase();
  if (lower.includes('suspend')) return { label: t('match.suspended'), color: palette.captain };
  if (lower.includes('injur') || lower.includes('knock') || lower.includes('fitness')) {
    return { label: t('match.injured'), color: palette.loss };
  }
  return { label: reason, color: palette.muted };
}

/** Absences card: injured / suspended players who miss this fixture. */
function InjuriesCard({ items }: { items: InjuryItem[] }) {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <Card className="overflow-hidden">
      <View className="flex-row items-center gap-2 border-b border-line bg-surface-2/50 px-4 py-2.5">
        <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-soft">
          {t('match.absences')}
        </AppText>
      </View>
      <View className="gap-2.5 px-4 py-3">
        {items.map((item, index) => {
          const style = reasonStyle(item.reason, t);
          return (
            <View key={index} className="flex-row items-center gap-2.5">
              <PlayerAvatar uri={item.photo} name={item.name} size={28} />
              <AppText variant="caption" weight="medium" className="flex-1 text-ink" numberOfLines={1}>
                {item.name}
              </AppText>
              <View className="rounded-full px-2 py-0.5" style={{ backgroundColor: `${style.color}22` }}>
                <AppText variant="micro" weight="bold" style={{ color: style.color }}>
                  {style.label}
                </AppText>
              </View>
            </View>
          );
        })}
      </View>
    </Card>
  );
}

/** Fallback when grid coordinates are missing: a plain numbered list. */
function PlainLineup({ lineup }: { lineup: LineupPayload }) {
  const { arabicNumerals, locale } = useLocale();
  return (
    <Card className="overflow-hidden">
      <View className="flex-row items-center justify-between border-b border-line bg-surface-2/50 px-4 py-2.5">
        <AppText variant="caption" weight="semibold" className="text-ink">
          {localizeTeamName(lineup.team, locale)}
        </AppText>
        {lineup.formation ? (
          <AppText variant="micro" weight="bold" tabular className="text-volt">
            {localizeDigits(lineup.formation, arabicNumerals)}
          </AppText>
        ) : null}
      </View>
      <View className="gap-1 px-4 py-3">
        {(lineup.startXI ?? [])
          .filter((entry) => entry?.player != null)
          .map((entry, index) => (
            <AppText key={index} variant="caption" className="text-ink">
              {entry.player.number ?? '-'} · {entry.player.name ?? '-'}
            </AppText>
          ))}
      </View>
    </Card>
  );
}

/**
 * Per-team lineups: a segmented selector of the two team names (NOT a single
 * face-to-face pitch). Picking a team shows ITS own pitch (photos + positions),
 * bench, and an absences card (injuries / suspensions) underneath.
 */
export function LineupsView({
  lineups,
  overlay,
  injuries,
  onPlayerPress,
}: {
  lineups: LineupPayload[];
  overlay?: LineupOverlay;
  injuries?: InjuriesBySide;
  /** Tap a pitch / bench player to open their match stat sheet (keyed by AF id). */
  onPlayerPress?: (afId: number) => void;
}) {
  const { locale } = useLocale();
  const [home, away] = lineups;
  const [side, setSide] = useState<'home' | 'away'>('home');
  const selected = side === 'home' ? home : away;

  // The match's RATED (Bzzoiro) players carry a provider_player_id we can turn
  // into a transparent cut-out portrait. The lineup pitch (API-Football) has no
  // such id, so we bridge by normalized name. This reads the route's fixture id
  // and the SAME cached useMatchXg query the match screen already mounted — so
  // it's deduped/free, not a second fetch. Enabled only when an id is present.
  const { id: fixtureId } = useLocalSearchParams<{ id: string }>();
  const xgQuery = useMatchXg(fixtureId, Boolean(fixtureId));
  const cutoutByName = useMemo(() => {
    const map = new Map<string, string>();
    for (const ratedPlayer of xgQuery.data?.players ?? []) {
      const cutout = bzzoiroCutout(ratedPlayer.provider_player_id);
      if (!cutout) continue;
      const key = normName(ratedPlayer.player_name);
      // First write wins: a rated row is unique per player, so name collisions
      // are vanishingly rare; keeping the first keeps the result deterministic.
      if (key && !map.has(key)) map.set(key, cutout);
    }
    return map;
  }, [xgQuery.data?.players]);

  if (!selected) return null;

  const hasGrid = parseGrid(selected).length > 0;
  const sideInjuries = injuries ? (side === 'home' ? injuries.home : injuries.away) : [];

  return (
    <CardBoundary>
      <View className="gap-4">
        {home && away ? (
          <Segmented
            options={[
              { value: 'home', label: localizeTeamName(home.team, locale) },
              { value: 'away', label: localizeTeamName(away.team, locale) },
            ]}
            value={side}
            onChange={setSide}
          />
        ) : null}

        {hasGrid ? (
          <SingleTeamPitch
            lineup={selected}
            overlay={overlay}
            cutoutByName={cutoutByName}
            onPlayerPress={onPlayerPress}
          />
        ) : (
          <PlainLineup lineup={selected} />
        )}
        <BenchCard lineup={selected} overlay={overlay} onPlayerPress={onPlayerPress} />
        <CoachCard lineup={selected} />
        <InjuriesCard items={sideInjuries} />
      </View>
    </CardBoundary>
  );
}
